import { SortCondition } from 'shared/enums';
import { TConsultationsInfo } from 'shared/types/consultations.types';
import { edpApiV1 } from './index';

export type ConsultationsRequest = {
  type: string | null;
  size?: number | null;
  page?: number | null;
  includeOwn?: boolean;
  order?: SortCondition;
  fio?: string;
  byId?: string;
  allAvailableOrganizations: boolean;
  personnelNumber?: string;
  positionName?: string;
};

export type ConsultationsResponse = {
  data: TConsultationsInfo[];
  pagination: {
    size: number;
    page: number;
    total: number;
  };
};

export type SingleConsultationResponse = {
  consultant: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
  };
  created: string;
  id: number;
  isClaim: boolean | null;
  isJustified: boolean | null;
  status: string;
  subtopic: {
    id: number | null;
    name: string | null;
  };
  topic: string;
  topicid: number;
  user: {
    id: number;
    name: string;
    lastName: string;
    middleName: string;
    personnelNumber: string | number;
    position: string;
    division: string;
  };
  violationreason: {
    id: number | null;
    name: string | null;
  };
  violator: {
    id: number | null;
    name: string | null;
  };
  feedback?: FeedbackResponse | null;
};

export type UpdateConsultationOrganizationRequest = {
  chatId: number | string;
};

export type UpdateConsultationResponse = {
  isClaim?: boolean;
  subtopicId?: number;
  isJustified?: boolean;
  violationReasonId?: number;
  data?: string;
};

export type ChangeConsultantRequest = {
  chatId: number;
  consultantId: number;
};

export type ChangeConsultantResponse = {
  data: string;
};

export type TakeToWorkConsultationResponse = {
  data: string;
};

export type SubTopicsResponse = [
  {
    id: number;
    topicId: number;
    name: string;
  },
];

export type VialatorsRequest = {
  violatorId?: number;
};

export type VialatorsResponse = {
  id?: number;
  name?: string;
  violationReasons?: [
    {
      id?: number;
      violatorId?: number;
      name?: string;
    },
  ];
}[];

export type GetMessagesRequest = {
  chatId: number;
  size: number;
  page: number;
};

export type GetMessagesResponse = {
  data: [
    {
      id: number;
      text: string;
      userId: number;
      chatId: number;
      consultantId: number;
      created: string;
    },
  ];
  pagination: {
    size: number;
    page: number;
    total: number;
  };
};

export type SendMessageRequest = {
  chatId: number;
  text: string;
};

export type SendMessageResponse = {
  id: number;
  text: string;
  userId: number;
  chatId: number;
  consultantId: number;
  created: string;
};

export type FeedbackResponse = {
  feedbackId: number;
  comment: string;
  stars: number;
  created: string;
};

export const consultationsApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    consultations: build.query<ConsultationsResponse, ConsultationsRequest>({
      query: payload => ({ url: '/v1/admin/consultation/chats', method: 'GET', params: payload }),
      providesTags: ['consultations'],
    }),
    singleConsultation: build.query<SingleConsultationResponse, number>({
      query: chatId => ({ url: `/v1/admin/consultation/chats/${chatId}`, method: 'GET' }),
      providesTags: ['singleConsultation'],
    }),
    updateConsultation: build.mutation<UpdateConsultationResponse, UpdateConsultationOrganizationRequest>({
      query: ({ chatId, ...body }) => ({ url: `/v1/admin/consultation/chats/${chatId}`, method: 'PATCH', body }),
      invalidatesTags: ['consultations'],
    }),
    changeConsultant: build.mutation<ChangeConsultantResponse, ChangeConsultantRequest>({
      query: ({ chatId, ...body }) => ({
        url: `/v1/admin/consultation/chats/${chatId}/change-consultant`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['consultations', 'singleConsultation'],
    }),
    getMessages: build.query<GetMessagesResponse, GetMessagesRequest>({
      query: ({ chatId, ...params }) => ({
        url: `/v1/admin/consultation/chats/${chatId}/messages`,
        method: 'GET',
        params,
      }),
      providesTags: ['getMessage'],
    }),
    sendMessage: build.mutation<SendMessageResponse, SendMessageRequest>({
      query: ({ chatId, ...body }) => ({
        url: `/v1/admin/consultation/chats/${chatId}/messages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['getMessage'],
    }),
    takeToWorkConsultation: build.mutation<TakeToWorkConsultationResponse, number>({
      query: chatId => ({
        url: `/v1/admin/consultation/chats/${chatId}/take`,
        method: 'POST',
      }),
      invalidatesTags: ['consultations'],
    }),
    subTopics: build.query<SubTopicsResponse, number>({
      query: topicId => ({ url: `/v1/admin/consultation/subtopics/${topicId}`, method: 'GET' }),
    }),
    violators: build.query<VialatorsResponse, VialatorsRequest>({
      query: params => ({ url: '/v1/admin/consultation/violators', method: 'GET', params }),
    }),
  }),
  overrideExisting: false,
});
export const {
  useConsultationsQuery,
  useLazyConsultationsQuery,
  useChangeConsultantMutation,
  useSingleConsultationQuery,
  useSubTopicsQuery,
  useUpdateConsultationMutation,
  useViolatorsQuery,
  useTakeToWorkConsultationMutation,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useSendMessageMutation,
} = consultationsApi;
